import { createRouter, createWebHistory } from 'vue-router'

import { StaffModule } from '@/enums/staff'
import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

/**
 * Six screens and a login, and every one of them is about the person reading it.
 *
 * # No route carries a permission, and that is the design
 *
 * docs/OSEBJE.md §13: self-service is the other side of being IN the record rather than
 * a right somebody grants, and the API narrows every one of these reads to the account
 * that made the call. A `meta.permission` here would be a second, weaker copy of a rule
 * the server already keeps — and the weaker copy is the one that eventually disagrees.
 *
 * What every route DOES carry is the licence. A `staff` route in a house that has not
 * bought the module answers 403 with code 19, which draws the locked notice over the
 * whole portal (libs/license.ts) — so a typed address is turned away before its first
 * call rather than after it.
 */
const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}

        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            // The presentation server's one-click sign-in (Logic/Demo.go, DemoSignIn.vue).
            path:      '/demo/:role',
            name:      'demo-sign-in',
            component: () => import('@/views/DemoSignIn.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // Where somebody lands: today. It is the root path rather than /home
            // because this is opened from a home-screen icon, not from a menu.
            path:      '/',
            name:      'home',
            component: () => import('@/views/home/Home.vue'),
            meta:      { requiresAuth: true, module: StaffModule.Staff },
        },
        {
            path:      '/clock',
            name:      'clock',
            component: () => import('@/views/clock/Clock.vue'),
            meta:      { requiresAuth: true, module: StaffModule.Staff },
        },
        {
            path:      '/rota',
            name:      'rota',
            component: () => import('@/views/rota/Rota.vue'),
            meta:      { requiresAuth: true, module: StaffModule.Staff },
        },
        {
            path:      '/leave',
            name:      'leave',
            component: () => import('@/views/leave/Leave.vue'),
            meta:      { requiresAuth: true, module: StaffModule.Staff },
        },
        {
            path:      '/documents',
            name:      'documents',
            component: () => import('@/views/documents/Documents.vue'),
            meta:      { requiresAuth: true, module: StaffModule.Staff },
        },
        {
            path:      '/profile',
            name:      'profile',
            component: () => import('@/views/profile/Profile.vue'),
            meta:      { requiresAuth: true, module: StaffModule.Staff },
        },

        /*
         * The reception portal's own addresses, answered here.
         *
         * Every notice this service has ever sent carries a link built from
         * `Logic/StaffNotice.go` — `/staff/my-rota` and `/staff/me?tab=vacation` — and
         * from the release that sets STAFF_PORTAL_URL those links point HERE instead
         * (see the API's staffNoticeLink). A letter sent last month carries the old
         * path and must still land on the right screen, and a deployment that sets the
         * variable after some letters have gone out has both in the world at once.
         *
         * Redirects rather than second route names, because there is one screen: a
         * second name for one component is a second thing to keep in the navigation,
         * the assistant context and anything else that switches on a route name.
         */
        { path: '/staff/my-rota',  redirect: { name: 'rota' } },
        { path: '/staff/my-time',  redirect: { name: 'clock' } },
        {
            // „Jaz“ in the reception portal is one page with four tabs, so the tab is in
            // the query and it decides which of the four screens here is meant.
            path: '/staff/me',
            redirect: (to) => {
                const tab = String(to.query.tab ?? '')

                if (tab === 'rota')      return { name: 'rota' }
                if (tab === 'vacation')  return { name: 'leave' }
                if (tab === 'documents') return { name: 'documents' }

                return { name: 'clock' }
            },
        },
    ],
})

router.beforeEach(async (to) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    if (loggedIn && to.name === 'login') return { name: 'home' }

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()

        return { name: 'login' }
    }

    if (!to.meta.requiresAuth || !loggedIn) return true

    // Imported here rather than at the top of the file on purpose: the session helper
    // reaches the fetch store, which reaches this router, and a static cycle would leave
    // one of the three undefined at evaluation time depending on which module the
    // bundler happened to start with.
    const { loadSession } = await import('@/libs/session')

    await loadSession()

    // The licence. A screen belonging to a module the house has not bought is not merely
    // empty here — its first call comes back 403 with code 19, and stores/fetch.ts turns
    // that into the notice App.vue draws over everything. Home carries the same gate, so
    // a house without the module reaches the notice rather than a redirect loop: the
    // guard lets the navigation through and App.vue replaces the layout.
    const module = to.meta.module as string | undefined

    if (module && !appStore.hasModule(module) && to.name !== 'home') return { name: 'home' }

    return true
})

/**
 * A screen whose code is no longer on the server.
 *
 * Every screen is loaded when it is first opened. Deploying a new version replaces those
 * files with differently named ones, so a browser tab that was open across a deploy asks
 * for a file that no longer exists — and the tap does nothing at all. No error, no
 * message. On a telephone left on a home screen for a fortnight this is the ordinary
 * case rather than the rare one.
 *
 * The tab heals itself by loading the new version once, at the address the person was
 * going to. The flag is what keeps that from becoming a loop when the failure is
 * something else — a portal that reloads for ever is worse than one that does nothing.
 */
const RELOAD_FLAG = 'pms:reloaded-for-chunk'

const isMissingChunk = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error)

    return /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(message)
}

router.onError((error, to) => {
    if (!isMissingChunk(error)) return

    if (sessionStorage.getItem(RELOAD_FLAG)) return

    sessionStorage.setItem(RELOAD_FLAG, '1')
    window.location.assign(to.fullPath)
})

// A navigation that worked is the proof the code is current, so the guard is cleared and
// the next deploy gets its one reload too.
router.afterEach(() => sessionStorage.removeItem(RELOAD_FLAG))

export default router
